import { useState, useEffect, useRef, useCallback, Suspense } from 'react'
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import ChunkLoadErrorBoundary, { ResetChunkReloadFlag } from './ChunkLoadErrorBoundary'
import PageLoading from './PageLoading'
import useIdlePrefetch from '../hooks/useIdlePrefetch'
import DraggablePopup from './DraggablePopup'
import api from '../services/api'
import { lockDateBadgeLabel, lockDateBadgeTitle } from '../utils/format'
import { safeOpenPopupWindow } from '../services/token'
import { timerWindowOptions } from '../utils/desktopVersion'
import { useTimerBarState, timerBarReplacesFab } from '../lib/desktopCapabilities'
import { useTimerCommandListener } from '../lib/timerCommands'
import { LEGACY_SLOT_KEY, isTimerSlotKey, resumeSlotKey, tagRouteWithPopupKey } from '../lib/timerSlots'
import timerStore, { FIRM_DEFAULT_BILLING_KEY, MESSAGES, rememberFirmDefaultBillingStatus } from '../lib/timerStore'
import useTimers from '../hooks/useTimers'
import TimerWidget from './TimerWidget'
import TimersPanel from './TimersPanel'
import { COMMAND_ACK_MS } from '../lib/timerWindows'
import { createOneClickGuard, openTimerFormFor, reportStoreResult, startOneClickTimer, toggleOneClickTimer } from '../hooks/useTimerEventBridge'
import { clampFabPosition, defaultFabPosition, fabPositionFromDrag, parseSavedFabPosition, readFabViewport, sameFabViewport } from '../utils/fabPosition'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useGlobalShortcuts } from '../hooks/useKeyboardShortcuts'
import useRealtime from '../hooks/useRealtime'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'
import GlobalSearch from './GlobalSearch'
import ContextMenu from './ContextMenu'
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp'
import ReportIssueButton from './ReportIssueButton'
import GlobalReminders from './GlobalReminders'
import ContextualHelp, { slugForPath } from './ContextualHelp'
import OnboardingTour from './OnboardingTour'
import { getRecentMatters } from '../lib/recentMatters'
import { recentClientsTracker, recentContactsTracker, recentMattersTracker } from '../lib/recentItems'
import { getPinnedMatters, unpinMatter, PINNED_MATTERS_EVENT } from '../lib/pinnedMatters'
import { SidebarQuickLists, RailQuickMenu } from './SidebarQuickLists'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import useReviewQueueBadge from '../hooks/useReviewQueueBadge'
import QuickIntakeModal from './QuickIntakeModal'
import { logRouteBreadcrumb } from '../lib/sentry'
import QuickAccessBar from './QuickAccessBar'
import { recordNavEntry, readNavHistory, resolveBack } from '../utils/navHistory'
// docs/specs/responsive-windowed.md — the menu fits the window: full menu,
// icon rail, or ☰ drawer (lib/shellLayout.js has the rules).
import {
  readSidebarPrefs, withSidebarChoice, writeSidebarChoice, sidebarModeFor, isSidebarPrefEvent,
} from '../lib/shellLayout'
import { useViewportTier, useOverlayMenu } from '../hooks/useShellLayout'
import RailTooltip from './RailTooltip'
// Feedback #835 — the sidebar's pages and its permission rule live in
// lib/navItems.js, shared with the customizable quick-access bar.
import {
  navGroups, isAdmin, hasPermission, canOpenNavItem, isTimeReviewConfigured,
  isNavGroupCollapsed, sidebarItemsForGroup, showInSidebar,
} from '../lib/navItems'

// The person's own open/closed choice per sidebar group, keyed by group label.
// A group with no stored choice uses its default (lib/navItems.js
// isNavGroupCollapsed): open, except FIRM SETUP.
const loadCollapsed = () => {
  try {
    const saved = localStorage.getItem('lextrack_nav_collapsed')
    const parsed = saved ? JSON.parse(saved) : {}
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch { return {} }
}

// The sidebar's grouped page list.  Its own component so the group, collapse
// and current-page rules can be rendered in a test
// (lib/sidebarNav.render.test.js) without mounting all of Layout.  It owns
// each group's open/closed state; Layout passes in everything else, and
// renders it unconditionally (the sidebar is hidden with CSS, never
// unmounted), so the state lives as long as the page.
export function SidebarNav ({
  user, isFeatureOn, desktopCollapsed = false, pathname = '', reviewQueueCount = 0,
  acctLockDate = null, onNavContextMenu, onNavClick,
}) {
  const [collapsed, setCollapsed] = useState(loadCollapsed)

  // Flip a group from what it SHOWS (its stored choice, else its default) —
  // `!collapsed[label]` would read an unset default-collapsed group as open
  // and make its first click a no-op.  The choice is stored per label, so it
  // sticks across reloads and wins over the group's default.
  const toggleGroup = (group) => {
    const next = { ...collapsed, [group.label]: !isNavGroupCollapsed(group, collapsed) }
    setCollapsed(next)
    try { localStorage.setItem('lextrack_nav_collapsed', JSON.stringify(next)) } catch { /* private mode etc. */ }
  }

  // The rail on a short window (760 px high or less): tighter rows, so more
  // icons fit between the header and footer (which tighten too); each icon
  // stays about 27 px tall, above the 24 px minimum target size.
  return (
    <nav className={`flex-1 ${desktopCollapsed ? 'px-1 [@media(max-height:760px)]:py-2' : 'px-4'} py-4 overflow-y-auto`}>
      {navGroups.map(group => {
        // Every page this person can open, except the Time Review Queue
        // while the firm doesn't use review (showInSidebar — it comes back
        // by itself once someone is set to require review, and stays drawn
        // while they're on that page).
        const visibleItems = group.items.filter(item =>
          canOpenNavItem(user, item, isFeatureOn) && showInSidebar(item, user, pathname))
        if (visibleItems.length === 0) return null
        const isCollapsed = isNavGroupCollapsed(group, collapsed)
        // Collapse-to-icons mode draws every item (no headers to toggle).
        // Otherwise a collapsed group still draws the page the person is
        // on, so the sidebar always shows where they are.
        const drawnItems = desktopCollapsed
          ? visibleItems
          : sidebarItemsForGroup(visibleItems, isCollapsed, pathname)
        return (
          <div key={group.label} className="mb-1">
            {/* Group header — hidden entirely in collapse-to-icons mode
                (title text is what makes it useful; a dot wouldn't help).
                Non-collapsed users still get the toggle. */}
            {!desktopCollapsed && (
              <button
                type="button"
                onClick={() => toggleGroup(group)}
                aria-expanded={!isCollapsed}
                className="flex items-center justify-between w-full px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-primary-300 hover:text-white transition-colors"
              >
                {group.label}
                <span className="text-primary-400 text-xs">{isCollapsed ? '▸' : '▾'}</span>
              </button>
            )}
            {/* Items — all of them in collapse-to-icons mode (the
                per-group toggle is meaningless when there are no
                labels).  Otherwise honor the toggle, keeping the
                current page visible in a collapsed group. */}
            {drawnItems.length > 0 && (
              <div className="space-y-0.5 mt-1">
                {drawnItems.map(item => {
                  const showReviewBadge = item.path === '/time-review-queue' && reviewQueueCount > 0
                  // Books-closed marker on /accounting.  AMBER, not red:
                  // a closed period is the correct steady state at month
                  // end, not an alert, and a red pill here would train
                  // people to ignore the colour that means "act".
                  // The nav item itself is already gated on
                  // can_view_accounting, so the badge inherits the right
                  // audience without a second check.
                  const lockLabel = item.path === '/accounting' ? lockDateBadgeLabel(acctLockDate) : null
                  const lockTitle = lockLabel ? lockDateBadgeTitle(acctLockDate) : null
                  // Icon-only: the link's text is just the emoji, so its
                  // name comes from aria-label (with what its corner dot
                  // means), and the rail's keyboard tooltip shows it
                  // (components/RailTooltip.js; the title covers the mouse).
                  const railName = desktopCollapsed
                    ? [item.name, showReviewBadge ? `${reviewQueueCount} pending review` : null, lockTitle].filter(Boolean).join(', ')
                    : undefined
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onContextMenu={(e) => onNavContextMenu?.(e, item)}
                      onClick={(e) => onNavClick?.(e, item)}
                      aria-label={railName}
                      data-rail-tip={desktopCollapsed ? item.name : undefined}
                      title={window.electronAPI?.openPopupWindow ? `${item.name} — right-click or Ctrl+click to open in new window` : item.name}
                      className={({ isActive }) =>
                        `relative flex items-center ${desktopCollapsed ? 'justify-center px-0 py-2 [@media(max-height:760px)]:py-1' : 'gap-3 px-4 py-2'} rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-primary-500 text-white'
                            : 'text-primary-100 hover:bg-primary-600'
                        }`
                      }
                    >
                      {/* Decorative: the link is named by its label (or its
                          aria-label in the rail), not "tear-off calendar". */}
                      <span aria-hidden="true">{item.icon}</span>
                      {!desktopCollapsed && item.name}
                      {/* Expanded sidebar: red pill with count next to the label. */}
                      {showReviewBadge && !desktopCollapsed && (
                        <span
                          className="ml-auto text-xs bg-red-600 text-white rounded-full px-2 py-0.5 font-medium"
                          aria-label={`${reviewQueueCount} pending review`}
                        >
                          {reviewQueueCount > 99 ? '99+' : reviewQueueCount}
                        </span>
                      )}
                      {/* Collapsed sidebar: small red dot corner marker (space-constrained). */}
                      {showReviewBadge && desktopCollapsed && (
                        <span
                          className="absolute top-1 right-1 w-2 h-2 bg-red-600 rounded-full"
                          aria-label={`${reviewQueueCount} pending review`}
                        />
                      )}
                      {/* Expanded: amber pill carrying the lock DATE.  The
                          day is part of the label on purpose — a month-only
                          summary misreads a mid-month close as a whole
                          closed month, and mid-month closes are legal. */}
                      {lockLabel && !desktopCollapsed && (
                        <span
                          className="ml-auto text-xs bg-amber-500 text-white rounded-full px-2 py-0.5 font-medium whitespace-nowrap"
                          title={lockTitle}
                          aria-label={lockTitle}
                        >
                          🔒 {lockLabel}
                        </span>
                      )}
                      {/* Collapsed: corner dot, matching the review-queue treatment. */}
                      {lockLabel && desktopCollapsed && (
                        <span
                          className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full"
                          title={lockTitle}
                          aria-label={lockTitle}
                        />
                      )}
                    </NavLink>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )
}

// Which form the browser's in-page timer popup shows next.  Pure, so the
// never-retarget rule is pinned without mounting Layout
// (Layout.timerHotfix.test.js).
//
// `current` = { nonce, key, seed, mounted }: `key` is the slot the in-page
// TimerWidget is bound to (null = an unbound form — nothing is timed until
// START), `nonce` is its React key.  A new nonce mounts a NEW form; the old
// one unmounts, which flushes its pending field merges.  A form that binds
// itself (START creates a slot, Save/Discard drop it, Continue seeds a resume
// slot) reports that through onRebind and is NOT remounted.
//
//   'prefill' — '+ Add Time', 'Setup & Start', the n/t shortcuts, Ctrl+K
//               'New time entry' (open-time-entry-popup): ALWAYS a new
//               unbound form with the matter / rate given.  Never the slot the
//               form is on — that timer keeps running in its slot.  (The old
//               in-page timer moved a running timer's time to the clicked
//               matter, Layout.js:1174 before 2026-10-14.)
//   'blank'   — the ⏱ button with nothing ticking, the panel's 'New entry
//               (type hours)': the form as it is when it is already unbound
//               (keeps what was typed), else a new blank one.
//   'slot'    — Fill in & save, the ⏱ button on the ticking timer, a row's
//               Resume: the form on that slot (the same form when it is
//               already on it).
// A paused timer untouched this long gets the load-time reminder toast.
const STALE_PAUSE_MS = 30 * 60 * 1000

export function nextInPageForm (current, request) {
  const cur = current || { nonce: 0, key: null, seed: null, mounted: false }
  const req = request || {}
  const fresh = (key, seed) => ({ nonce: (Number(cur.nonce) || 0) + 1, key, seed: seed || null, mounted: true })
  if (req.kind === 'prefill') {
    const matterId = req.matterId != null && req.matterId !== '' ? String(req.matterId) : ''
    const rate = req.rate != null && req.rate !== '' ? String(req.rate) : ''
    return fresh(null, { matterId, rate })
  }
  if (req.kind === 'blank') return cur.mounted && cur.key == null ? cur : fresh(null, null)
  if (req.kind === 'slot') {
    if (!isTimerSlotKey(req.key)) return cur
    return cur.mounted && cur.key === req.key ? cur : fresh(req.key, req.seed)
  }
  return cur
}

// A person's ⏸ opens the paused timer's form (James, 2026-10-15), but never
// at the cost of work the in-page form holds: an unbound form with a typed
// description or hours is stored nowhere (a swap loses it), and a form saving
// another timer would lose its result.  That form stays, shown, with this
// toast (no matter name — Rule 1.6).  The pause stands either way.
export const PAUSE_FORM_KEPT = Object.freeze({
  draft: 'Timer paused. Save or cancel the entry you are typing, then use Fill in & save on the paused timer (Timers).',
  saving: 'Timer paused. Another entry is still saving; then use Fill in & save on the paused timer (Timers).'
})

// The in-page form's frame.  It owns the title's 1 s tick ('Timer — H:MM:SS'
// from the slot, or 'New Time Entry'), so Layout itself never re-renders every
// second; `children` is the element Layout built, so this re-render does not
// re-render the form.  Hidden (× / − / another popup opening) keeps the form
// MOUNTED — what was typed survives — and the slot keeps the time.
function InPageTimerPopup ({ visible, slotKey, onHide, boxRef, children }) {
  useTimers({ tick: visible && !!slotKey, reconcile: false })
  let row = null
  if (slotKey) {
    try { row = timerStore.getTimer(slotKey) } catch { row = null }
  }
  const title = row ? `Timer — ${timerStore.formatElapsed(row.elapsed)}` : 'New Time Entry'
  return (
    <div hidden={!visible} ref={boxRef}>
      <DraggablePopup
        title={title}
        onClose={onHide}
        /* × and − only hide the form: the timer lives in its slot. */
        onMinimize={onHide}
        width={480}
        suppressTimerMinimize={true}
        /* Feedback #514: remember where user last dragged the timer popup. */
        positionKey="timer">
        {children}
      </DraggablePopup>
    </div>
  )
}

// This window's timer watcher.  useTimers runs the debounced one-running
// reconciler here (every new-bundle window with a uid runs it) and re-reads
// on every slot write and owner heartbeat — every 5 s for each window or tab
// holding a ticking timer.  It lives in this null child so those re-reads
// re-render only it: Layout hears just `anyTicking` (the ⏱ pulse), and
// re-renders only when that flips.  Handlers read the timer list when they
// run (timerStore.listMyTimers), never from Layout state.
function TimersWatch ({ onAnyTicking }) {
  const { anyTicking } = useTimers({ tick: false })
  useEffect(() => { onAnyTicking(anyTicking) }, [anyTicking, onAnyTicking])
  return null
}

// My visible timers right now (the token's uid), for an event handler.
function readMyTimers () {
  try { return timerStore.listMyTimers() } catch { return [] }
}

// A 'focus' command for the legacy slot is heard by this window even when
// this window sent it (lib/timerCommands dispatches the same command here
// too), and opening the legacy timer can itself send one (openTimerForm on a
// timer still on old code, or one held by a window that recorded no popupKey,
// and again on that window's silence 1.5 s later).  One answer per
// LEGACY_FOCUS_MS, so it can never feed itself.
const LEGACY_FOCUS_MS = COMMAND_ACK_MS + 500

// The storage keys behind the sidebar's Pinned and Recent lists
// (lib/recentItems.js, lib/pinnedMatters.js).
const SIDEBAR_RECENT_KEYS = new Set([
  'lextrack_recent_matters', 'lextrack_recent_clients', 'lextrack_recent_contacts', 'lextrack_pinned_matters'
])

export default function Layout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { isEnabled: isTopLevelFeatureOn } = useFeatureFlags()
  useGlobalShortcuts()
  // Open a realtime WebSocket for live nudges — invoice-paid pings, new
  // matter comms, etc.  Only enabled when there's a logged-in user (the
  // hook itself checks the token, so this flag is defense-in-depth).  Also
  // gated to non-portal routes so a staff who navigated to a portal preview
  // URL by mistake doesn't try to open a WS with a staff JWT to a portal-
  // scoped endpoint (currently the WS route is staff-only anyway, but the
  // gate future-proofs when portal-scoped WS ships).  Finally: if the firm
  // has disabled the `realtime` feature flag, don't open the connection at
  // all — GlobalReminders' 60s polling still surfaces reminders in that mode.
  useRealtime({ enabled: !!user && !location.pathname.startsWith('/portal') && isTopLevelFeatureOn('realtime') })
  // Time review queue badge count — only fires the underlying poll when the
  // caller has the time_entries permission AND the firm uses time review
  // (GET /api/auth/me `time_review_configured`).  With review not set up the
  // queue is empty by definition, so the 60 s poll (about 12% of all API
  // requests on 2026-10-12) only ever fetched 0, and the sidebar leaves the
  // queue out until review is set up (SidebarNav / showInSidebar).  The hook
  // applies both gates itself too.
  const { count: reviewQueueCount } = useReviewQueueBadge({
    enabled: hasPermission(user, 'time_entries') && isTimeReviewConfigured(user),
  })
  // Sentry route breadcrumbs — a crash's stack trace now knows what page
  // the user was on.  No-op when Sentry isn't initialized.
  useEffect(() => { logRouteBreadcrumb(location.pathname) }, [location.pathname])
  // Feedback #830 — remember which in-app page each history entry shows, so
  // the ← Back button below can tell when the entry behind it is NOT one of
  // ours (the sign-in page, or unknown) and go somewhere sensible instead.
  useEffect(() => {
    recordNavEntry(window.history.state?.idx ?? 0, location.pathname)
  }, [location.key, location.pathname])

  // Note: ResetChunkReloadFlag is rendered as a sibling of <Outlet />
  // inside the inner Suspense (see below) rather than called here at
  // Layout top-level.  That way the flag only clears when the lazy
  // content has actually mounted successfully — a top-level call
  // would clear the flag even during error-boundary fallback UI and
  // allow an auto-reload loop on a truly broken deploy (post-code
  // review by Kimi + Grok).

  // T2-B Phase 1 — idle-prefetch lazy pages after mount so users can
  // navigate to them offline (Electron desktop app; users work on planes).
  // Fires via requestIdleCallback, staggered by 200ms per import so a slow
  // connection doesn't get 20 parallel fetches slammed at once.  Silently
  // no-ops on error (real chunk-load failures still hit
  // ChunkLoadErrorBoundary if the user hits the route pre-prefetch).
  //
  // Curated list (not exhaustive) — the top-N most-navigated lazy pages
  // plus admin-only pages when the user is admin.  Adding a new lazy page
  // to App.js does NOT auto-add it here; extend this list manually when a
  // new page becomes a common destination.
  useIdlePrefetch(() => {
    const list = [
      // T2-B Phase 2 — the newly-lazy pages come FIRST since they're
      // the most-navigated (billing/timekeeping/reception flows).
      () => import('../pages/Calendar'),
      () => import('../pages/TimeEntries'),
      () => import('../pages/InvoiceDetail'),
      () => import('../pages/Receipts'),
      // Existing lazy pages (pre-T2-B)
      () => import('../pages/Reports'),
      () => import('../pages/Trust'),
      () => import('../pages/BatchInvoicing'),
      () => import('../pages/ConflictChecker'),
      () => import('../pages/Discovery'),
      () => import('../pages/Deadlines'),
      () => import('../pages/AttorneyProductivity'),
      () => import('../pages/CustomReports'),
      () => import('../pages/CourtRules'),
      () => import('../pages/SearchResults'),
      () => import('../pages/ActivityFeed'),
      () => import('../pages/Help'),
      () => import('../pages/ContactDuplicates'),
      () => import('../pages/ClientMerge'),
      () => import('../components/PopupLayout'), // pre-warm popup shell
    ]
    if (user?.role === 'admin' || user?.permissions?.is_admin) {
      list.push(
        () => import('../pages/AdminHome'),
        () => import('../pages/AuditLog'),
        () => import('../pages/Import'),
        () => import('../pages/Feedback'),
        () => import('../pages/TimeEntryDuplicates'),
        () => import('../pages/MatterDuplicates'),
      )
    }
    // Newly-lazy admin / intake / config pages.  Gated on the same
    // permission as the matching route in App.js so a user never spends
    // bandwidth on a chunk that would only render a 403 for them.
    if (hasPermission(user, 'invoices')) list.push(() => import('../pages/Invoices'))
    if (hasPermission(user, 'leads')) list.push(() => import('../pages/Leads'))
    if (hasPermission(user, 'leads')) list.push(() => import('../pages/ReferralSources'))
    if (hasPermission(user, 'leads')) list.push(() => import('../pages/ConsultBookings'))
    if (hasPermission(user, 'can_manage_leads')) list.push(() => import('../pages/LeadAssignmentRules'))
    if (hasPermission(user, 'can_manage_intake_forms')) list.push(() => import('../pages/IntakeForms'))
    if (hasPermission(user, 'can_manage_intake_forms')) list.push(() => import('../pages/IntakeFormEmbed'))
    if (hasPermission(user, 'can_manage_engagement_letters')) list.push(() => import('../pages/EngagementTemplates'))
    if (hasPermission(user, 'can_manage_email_templates')) list.push(() => import('../pages/EmailTemplates'))
    if (hasPermission(user, 'can_manage_email_sequences')) list.push(() => import('../pages/EmailSequences'))
    if (hasPermission(user, 'matters')) list.push(() => import('../pages/MatterTemplates'))
    if (hasPermission(user, 'time_entries')) list.push(() => import('../pages/TimeReviewQueue'))
    if (hasPermission(user, ['can_manage_leads', 'can_edit_contacts'])) list.push(() => import('../pages/Tags'))
    if (hasPermission(user, 'payments')) list.push(() => import('../pages/Payments'))
    if (hasPermission(user, 'downloads')) list.push(() => import('../pages/Downloads'))
    if (hasPermission(user, 'team')) list.push(() => import('../pages/Team'))
    if (isAdmin(user)) list.push(() => import('../pages/Webhooks'))
    if (isAdmin(user)) list.push(() => import('../pages/Settings'))
    return list
  })
  // Accounting period lock, for the sidebar badge on /accounting.
  //
  // DECLARED HERE, above the effect that sets it — a useState below its own
  // setter is a runtime ReferenceError that `node --check` does not catch, and
  // this repo has hit that six times.
  //
  // SOURCE NOTE, because the obvious endpoint is a trap: the natural-looking
  // `GET /api/accounting/period/status` DOES return accounting_lock_date, but it
  // sits behind accountingEnabledCheck (accounting.js), which 400s whenever
  // accounting_enabled is false — and its bootstrap exemption covers only
  // `/coa*`, so admins are refused too.  Layout renders on every page, so
  // polling it would put a 400 (or, on a pre-Ship-7a JWT, a firmScope 401) on
  // every page load of the whole app.  `GET /api/settings` is a plain
  // `SELECT * FROM firm_settings`, and stripStripeSecrets removes only the
  // Stripe/IRS columns, so both fields are ALREADY in a payload this component
  // ALREADY fetches below.  The badge therefore costs zero extra requests and
  // touches zero new gates.
  //
  // `undefined` on the global means "not fetched yet"; `null` means "fetched,
  // nothing to show".  The distinction matters — a lock date of null is a
  // legitimate loaded state (no period closed), so truthiness cannot stand in
  // for loadedness the way it does for the two globals beside it.
  const [acctLockDate, setAcctLockDate] = useState(() => window.__lextrackAcctLockDate ?? null)
  // Amicus-parity (2026-08-04) — cache firm's default billing status on a
  // window global so all 3 timer surfaces (this popup + TimeEntries manual +
  // TimerWidget pop-out) can init billing_status without each fetching
  // /api/settings independently.  Same pattern as `__lextrackFirmTz`.
  //
  // The desktop floating bar never loads /api/settings, so its one-click
  // start read 'billable' while the firm default is 'no-charge-show'.  Each
  // value that came from the server (this fetch, or Settings saving a new
  // one — both fire 'lextrack-defaults-loaded') is also kept in storage
  // (timerStore FIRM_DEFAULT_BILLING_KEY), where timerStore's start path
  // reads it when the window global is absent; written again after a
  // "Reset UI preferences" (key-null event) or another window removes it.
  const firmBillingRef = useRef(null)
  useEffect(() => {
    const keep = (v) => { if (rememberFirmDefaultBillingStatus(v)) firmBillingRef.current = v }
    const onLoaded = () => keep(window.__lextrackDefaultBillingStatus)
    const onStorage = (e) => {
      if (!firmBillingRef.current) return
      if (!e || e.key === null || (e.key === FIRM_DEFAULT_BILLING_KEY && e.newValue == null)) keep(firmBillingRef.current)
    }
    window.addEventListener('lextrack-defaults-loaded', onLoaded)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('lextrack-defaults-loaded', onLoaded)
      window.removeEventListener('storage', onStorage)
    }
  }, [])
  useEffect(() => {
    if (!user) return
    // The lock date joins the early-return condition rather than riding on it.
    // Otherwise a re-login (Layout unmounts and remounts while the two globals
    // stay warm) short-circuits here, leaving acctLockDate at its initial value
    // forever — a badge that appears after a hard refresh and never after a
    // sign-out/sign-in, which reads as flakiness rather than a wiring bug.
    if (window.__lextrackDefaultBillingStatus && window.__lextrackFirmTz &&
        window.__lextrackAcctLockDate !== undefined) return
    api.get('/api/settings').then(r => {
      const s = r.data?.default_time_entry_billing_status || 'billable'
      window.__lextrackDefaultBillingStatus = s
      // The firm's calendar is the firm's calendar: an event at 9am in San
      // Diego is at 9am wherever the laptop is opened.  Populate the firm
      // timezone from this same request so the calendar renders the firm's
      // clock even for an attorney working abroad.  Previously this global was
      // only set if the user happened to visit Time Entries first, and every
      // other page silently fell back to the browser's zone.
      const tz = r.data?.timezone
      if (tz) window.__lextrackFirmTz = tz
      // Gated on accounting_enabled, not just on the date being present: a firm
      // that has not opted into accounting must never see a books-closed badge,
      // and a stale lock date can outlive the flag being switched back off.
      const lock = r.data?.accounting_enabled ? (r.data?.accounting_lock_date || null) : null
      window.__lextrackAcctLockDate = lock
      setAcctLockDate(lock)
      window.dispatchEvent(new Event('lextrack-defaults-loaded'))
    }).catch(() => { window.__lextrackDefaultBillingStatus = 'billable' })
  }, [user])
  const toast = useToast()
  const ask = useAsk()
  // Realtime event handler — bind the WS incoming events to user-visible
  // toasts.  Kept in Layout so it's always mounted; other components can
  // subscribe to the same 'realtime' window event for finer-grained
  // reactions (e.g. MatterDetail refreshing its comms list live).
  useEffect(() => {
    const handler = (e) => {
      const { type, payload } = e.detail || {}
      // Broadcasts are DIFFERENT — they're intentional admin announcements
      // meant for everyone including the sender.  Don't self-echo-suppress
      // + render as a longer-lived, more prominent toast (severity-colored).
      if (type === 'broadcast.message') {
        const msg = `📢 ${payload.from}: ${payload.message}`
        // Longer display duration for broadcasts (12s) — they usually carry
        // actionable content the recipient wants time to read.
        if (payload.severity === 'urgent' && toast?.error) {
          toast.custom({ type: 'error',  title: 'Firm broadcast', message: msg, duration: 12000 })
        } else if (payload.severity === 'warn' && toast?.warn) {
          toast.custom({ type: 'warn',   title: 'Firm broadcast', message: msg, duration: 12000 })
        } else if (toast?.custom) {
          toast.custom({ type: 'info',   title: 'Firm broadcast', message: msg, duration: 12000 })
        }
        return
      }
      // Suppress toasts for events fired by the current user — the sender
      // already sees the local save-success feedback and doesn't need a
      // duplicate.  Server tags every event with a `by_id` field (user id —
      // stable identifier that never breaks even when the JWT lacks a name
      // claim) plus a `by` string for display.  Older publishes only set
      // legacy actor strings (paid_by/voided_by/closed_by); fall back to
      // matching those against the user's name/email so pre-6b payloads
      // still get suppressed correctly.
      const isSelfEcho = (() => {
        if (!payload || !user) return false
        if (payload.by_id != null && payload.by_id === user.id) return true
        const actor = payload.by || payload.paid_by || payload.voided_by || payload.closed_by
        if (!actor) return false
        return actor === user.name || actor === user.email
      })()
      if (isSelfEcho) return
      if (type === 'invoice.paid' && toast?.success) {
        toast.success(`💰 Invoice #${payload.invoice_number} paid ($${(payload.amount || 0).toFixed(2)}) by ${payload.paid_by}`)
      } else if (type === 'invoice.voided' && toast?.warn) {
        toast.warn(`⛔ ${payload.voided_by} voided invoice #${payload.invoice_number} ($${(payload.total || 0).toFixed(2)})`)
      } else if (type === 'communication.created' && toast?.info) {
        toast.info(`📞 ${payload.by} logged a ${payload.comm_type}: "${payload.subject || '(no subject)'}"`)
      } else if (type === 'matter.created' && toast?.info) {
        toast.info(`📁 ${payload.by} created matter: "${payload.title}"`)
      } else if (type === 'matter.closed' && toast?.warn) {
        toast.warn(`🔒 ${payload.closed_by} closed matter: "${payload.title}"`)
      } else if (type === 'event.created' && toast?.info) {
        toast.info(`📅 ${payload.by} added event: "${payload.title}"`)
      } else if (type === 'note.created' && toast?.info) {
        toast.info(`📝 ${payload.by} added a note: "${payload.snippet}${(payload.snippet || '').length >= 80 ? '…' : ''}"`)
      } else if (type === 'discovery.created' && toast?.info) {
        toast.info(`🔍 ${payload.by} logged discovery: ${payload.type || 'set'} ${payload.set_number ? `#${payload.set_number}` : ''}`)
      } else if (type === 'trust.deposit' && toast?.success) {
        toast.success(`💵 ${payload.by} deposited $${(payload.amount || 0).toFixed(2)} to trust${payload.source ? ` (${payload.source})` : ''}`)
      } else if (type === 'trust.disbursement' && toast?.info) {
        toast.info(`💸 ${payload.by} disbursed $${(payload.amount || 0).toFixed(2)} from trust${payload.payee ? ` to ${payload.payee}` : ''}`)
      } else if (type === 'task.assigned' && toast?.info) {
        const when = payload.start_time ? ' on ' + new Date(payload.start_time).toLocaleDateString() : ''
        toast.info(`📌 ${payload.by || 'Someone'} added you to "${payload.title}"${when}`)
      } else if (type === 'lead.assigned' && toast?.info) {
        toast.info(`🎯 ${payload.by || 'Someone'} assigned lead "${payload.lead_name}" to you`)
      } else if (type === 'lead.assigned.bulk' && toast?.info) {
        toast.info(`🎯 ${payload.by || 'Someone'} assigned ${payload.count} leads to you`)
      } else if (type === 'lead.phone_intake' && toast?.info) {
        // Ship 7j (feedback #743) — new phone-intake landed.  Server scopes
        // the audience to the designated reviewer + admins, so we don't
        // need a client-side gate here.  Self-echo already suppressed by
        // the by_id check above (intake-taker won't get their own toast).
        const name = payload.lead_name || `${payload.first_name || ''} ${payload.last_name || ''}`.trim() || 'unnamed lead'
        const who = payload.by || 'Someone'
        toast.info(`📞 New phone intake by ${who}: ${name}`)
      } else if (type === 'lead.phone_intake_reminder' && toast?.warn) {
        // Cron path — payload.by is null.  Fires the daily nudge at the
        // firm's configured hour until all pending intakes are marked
        // reviewed.  Uses `warn` tone since it's a reviewer-attention ask.
        const n = Number(payload.count || 0)
        const plural = n === 1 ? '' : 's'
        toast.warn(`⏰ ${n} phone intake${plural} still awaiting your review`)
      } else if (type === 'lead.reviewed' && toast?.success) {
        // Ship 7j — someone (usually the reviewer) marked a phone-intake
        // lead reviewed.  Server publishes to admin + the intake-creator
        // audience so the intake taker knows their queue moved.
        const name = payload.lead_name || `Lead #${payload.lead_id || '?'}`
        const who = payload.by || 'Reviewer'
        toast.success(`✓ ${name} marked reviewed by ${who}`)
      } else if (type === 'questionnaire.submitted' && toast?.success) {
        // Questionnaire ship (2026-08-29) — a potential client submitted their
        // intake questionnaire.  Payload is METADATA ONLY (panel C4 / #27):
        // ids + status + deep link — never a name, narrative, or checklist.
        // by_id is null (client-originated), so self-echo never suppresses it.
        toast.success(`📝 Intake questionnaire submitted for lead #${payload.lead_id || '?'} — open Leads to review`)
      } else if (type === 'questionnaire.locked' && toast?.warn) {
        // Questionnaire v2 (decision 8) — ten wrong link attempts locked a
        // questionnaire for 15 minutes; it unlocks by itself, and a staff
        // resend unlocks it now.  Metadata only (ids + locked_until), by_id
        // null (nobody on staff caused it).
        toast.warn(`🔒 Intake questionnaire link for lead #${payload.lead_id || '?'} locked for 15 minutes after repeated wrong attempts — it unlocks by itself; ↻ Resend unlocks it now`)
      } else if (type === 'lead.decided' && toast?.info) {
        // Questionnaire ship — an attorney approved / declined a lead.  Same
        // metadata-only discipline; by_id self-echo handled above.
        const verb = payload.decision === 'approved'
          ? (payload.mode === 'engagement_letter' ? 'approved (agreement sent)' : 'approved (portal invite sent)')
          : 'declined (non-engagement letter sent)'
        toast.info(`⚖️ ${payload.by || 'Someone'} ${verb} lead #${payload.lead_id || '?'}`)
      } else if (type === 'time.review-request-changes' && toast?.warn) {
        toast.warn(`↩︎ ${payload.by || 'Reviewer'} requested changes on your ${Number(payload.hours || 0).toFixed(1)}hr entry: "${String(payload.review_notes || '').slice(0, 80)}"`)
      } else if (type === 'trust.dispute_resolved' && toast?.success) {
        toast.success(`⚖ ${payload.by || 'Admin'} resolved trust dispute on $${Number(payload.amount || 0).toFixed(2)} deposit (${payload.resolution})`)
      } else if (type === 'invoice.credit_issued' && toast?.info) {
        toast.info(`💳 ${payload.by || 'Someone'} issued ${payload.credit_number} for $${Number(payload.amount || 0).toFixed(2)} against #${payload.invoice_number}`)
      } else if (type === 'invoice.reminder_sent' && toast?.info) {
        // Cron path has by:null — always show; attribution differs for automated vs manual sends.
        const attrib = payload.by ? ' by ' + payload.by : ' (automated)'
        toast.info(`📮 Past-due reminder sent for #${payload.invoice_number} (${payload.threshold_days}d)${attrib}`)
      } else if (type === 'matter.blocked') {
        // Server pushes matter.blocked ONLY to the affected user (either the
        // matter is deleted / closed with revoke, or a per-matter permission
        // was revoked / the visibility group changed).  Scrub every stale
        // client-side cache immediately: pinned, recent, and the timers on
        // that matter, whose next Save would 404.  Post the audit to the user
        // so they know why the sidebar item just vanished.
        //
        // Timers (firm decision 2026-10-13 — keep the time): my timers on the
        // matter PAUSE and lose matter, title and phase ('No matter yet'), so
        // the person can pick another matter or discard them; my resume
        // timers on it are removed (they could only PUT to an entry I can no
        // longer reach).  A timer still written by old code is removed, as
        // before.  Everything goes through lib/timerStore.js; other people's
        // timers on this computer are untouched.
        let timerNote = ''
        try {
          const matterId = payload && Number.isFinite(Number(payload.matter_id))
            ? Number(payload.matter_id)
            : null
          if (matterId != null) {
            try { unpinMatter(matterId) } catch (_) { /* ignore */ }
            try { recentMattersTracker.remove(matterId) } catch (_) { /* ignore */ }
            try {
              const r = timerStore.blockMatter(matterId)
              const closed = (r.removed || []).length
              const cleared = (r.cleared || []).length
              let removed = 0
              // Old-code timers: removed as before (discard refuses one an
              // old-bundle window holds — that window keeps it).
              const keys = []
              for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i)
                if (isTimerSlotKey(k)) keys.push(k)
              }
              for (const k of keys) {
                const row = timerStore.getTimer(k)
                if (!row || row.v2 || !row.mine || row.matterId !== String(matterId)) continue
                const d = timerStore.discard(k, { force: true })
                if (d.ok && d.changed) removed++
              }
              if (cleared > 0) timerNote += ` Your timer${cleared === 1 ? ' on it was' : 's on it were'} paused and ${cleared === 1 ? 'its' : 'their'} matter cleared — the time is kept; pick another matter or discard ${cleared === 1 ? 'it' : 'them'}.`
              if (closed > 0) timerNote += ` ${closed === 1 ? 'A timer' : `${closed} timers`} updating ${closed === 1 ? 'an entry' : 'entries'} on it ${closed === 1 ? 'was' : 'were'} closed — the saved hours are unchanged.`
              if (removed > 0) timerNote += ` ${removed === 1 ? 'A timer' : `${removed} timers`} on it from before the update ${removed === 1 ? 'was' : 'were'} removed.`
            } catch (err) {
              // The pins and recents above are already scrubbed; a timer
              // failure must not hide the notice below.
              console.error('matter.blocked: timer cleanup failed', err)
            }
          }
        } catch (_) { /* ignore — cleanup path never throws */ }
        if (toast?.warn) {
          toast.warn(`You have been removed from access to a matter. Contact your admin if this was in error.${timerNote}`)
        }
      }
    }
    window.addEventListener('realtime', handler)
    return () => window.removeEventListener('realtime', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // Route-based document.title fallback — pages that don't call
  // usePageTitle() themselves get a reasonable default derived from the
  // pathname.  Individual pages can override this by calling usePageTitle()
  // (their unmount cleanup runs AFTER our next mount so the pathname-based
  // default wins as they navigate away).  Detail pages (e.g. /matters/:id)
  // set their own title with the record's name once loaded.
  useEffect(() => {
    const p = location.pathname
    const routeTitles = {
      '/dashboard':      'Dashboard',
      '/dailies':        'Dailies',
      '/matters':        'Matters',
      '/clients':        'Clients',
      '/contacts':       'Contacts',
      '/leads':          'Leads',
      '/intake-forms':   'Intake Forms',
      '/engagement-templates': 'Engagement Templates',
      '/email-templates': 'Email Templates',
      '/email-sequences': 'Email Sequences',
      '/time-entries':   'Time Entries',
      '/calendar':       'Calendar',
      '/deadlines':      'Deadlines',
      '/tasks':          'To-Do',
      '/discovery':      'Discovery',
      '/invoices':       'Invoices',
      '/payments':       'Payments',
      '/receipts':       'Receipts',
      '/billing':        'Billing',
      '/batch-invoicing':'Batch Invoicing',
      '/trust':          'Trust Accounting',
      '/accounting':     'Chart of Accounts',
      '/reports':        'Reports',
      '/attorney-productivity': 'Attorney Productivity',
      '/custom-reports':  'Custom Reports',
      '/custom-fields':   'Custom Fields',
      '/roles':           'Roles',
      '/tags':            'Tags',
      '/consult-bookings': 'Consult Bookings',
      '/lead-assignment-rules': 'Lead Assignment Rules',
      '/sol':            'SOL Tracker',
      '/conflicts':      'Conflicts',
      '/communications': 'Communications',
      '/court-rules':    'Court Rules',
      '/team':           'Team',
      '/settings':       'Settings',
      '/profile':        'Profile',
      '/admin':          'Admin Tools',
      '/audit-log':      'Audit Log',
      '/feedback':       'Feedback',
      '/import':         'Import',
      '/downloads':      'Downloads',
      '/help':           'Help',
      '/search':         'Search',
      '/activity-feed':  'Activity Feed',
      '/webhooks':       'Webhooks',
      '/referral-sources': 'Referral Sources',
      '/time-review-queue': 'Time Review Queue',
    }
    // Exact match first, then prefix-based fallback (matter/client detail
    // pages override with their own usePageTitle once data loads)
    let title = routeTitles[p]
    if (!title) {
      const seg = p.split('/').filter(Boolean)[0]
      if (seg) title = routeTitles['/' + seg]
    }
    document.title = title ? `${title} · Omnia Practice Management` : 'Omnia Practice Management'
  }, [location.pathname])
  const { sidebar_style, sidebar_width } = useTheme()
  // Full-screen calendar (Hestia).  A page asks for the chrome to get out of
  // the way by dispatching `lextrack-focus-mode`; Calendar.js always sends
  // {on:false} on unmount, so navigating away restores the sidebar.
  //
  // This deliberately does NOT touch `lextrack_sidebar_collapsed` — that is the
  // user's own persisted preference, and leaving full screen must give it back
  // exactly as it was rather than leaving her collapsed everywhere.
  //
  // Declared above the menu state below, which reads it (a const read before
  // its declaration throws only at runtime).
  const [focusMode, setFocusMode] = useState(false)
  useEffect(() => {
    const onFocusMode = (e) => setFocusMode(!!e?.detail?.on)
    window.addEventListener('lextrack-focus-mode', onFocusMode)
    return () => window.removeEventListener('lextrack-focus-mode', onFocusMode)
  }, [])
  // The side menu fits the window (docs/specs/responsive-windowed.md, James
  // 2026-10-19 "Rearrange to fit"; rules in lib/shellLayout.js):
  //   wide (>= 1280 px)    the full menu; « collapses it to the icon rail
  //   medium (768-1279)    the icon rail; » expands it to the full menu
  //   narrow (< 768 px)    a ☰ button; the menu opens as a drawer over the page
  // The «/» choice is the person's own and is remembered PER TIER in
  // localStorage (lextrack_sidebar_collapsed for wide windows — the key that
  // always held it — and lextrack_sidebar_collapsed_medium), so collapsing a
  // wide window never forces the rail on it once it is wide again, and
  // expanding on a medium window is kept for medium windows.  Independent of
  // the theme's sidebar_width/sidebar_style.
  const viewportTier = useViewportTier()
  const [sidebarPrefs, setSidebarPrefs] = useState(() => readSidebarPrefs())
  const sidebarMode = sidebarModeFor(viewportTier, sidebarPrefs)
  const isDrawer = sidebarMode === 'drawer'
  const isRail = sidebarMode === 'rail'
  // The menu drawn OVER the page: null (none), 'menu' (the ☰ drawer) or
  // 'search' (Ctrl+K / 🔍 while the search box is not on screen — the drawer,
  // or the rail opened full width over the page with the box focused).
  // Never in the full mode, where the menu is already on screen — except in
  // Calendar's full screen, which hides it.
  const [menuOverlay, setMenuOverlay] = useState(null)
  const overlayOpen = menuOverlay !== null && (sidebarMode !== 'full' || focusMode)
  // SidebarNav's icon-only flag: the rail, unless it is open over the page.
  const desktopCollapsed = isRail && !overlayOpen
  const toggleDesktopCollapsed = () => {
    const collapsed = sidebarMode !== 'rail'
    writeSidebarChoice(viewportTier, collapsed)
    setSidebarPrefs((prev) => withSidebarChoice(prev, viewportTier, collapsed))
  }
  // Another tab's choice, and "Reset UI preferences" (a key-null storage
  // event — lib/uiPrefs.js), apply at once.  Other keys are ignored, so timer
  // heartbeats from other windows never re-render the shell.
  useEffect(() => {
    const onStorage = (e) => {
      if (!isSidebarPrefEvent(e)) return
      setSidebarPrefs((prev) => {
        const next = readSidebarPrefs()
        return next.wideCollapsed === prev.wideCollapsed && next.mediumCollapsed === prev.mediumCollapsed ? prev : next
      })
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
  const closeMenuOverlay = useCallback(() => setMenuOverlay(null), [])
  // Ctrl+K and "/" (focus-global-search, hooks/useKeyboardShortcuts.js) focus
  // the search box, which is on screen only in the full menu beside the page.
  // In the rail (where it is not drawn), the closed drawer (inert) and
  // Calendar's full screen (menu hidden), open the menu over the page with the
  // box focused.  Reads the state through a ref: the listener is registered
  // once.
  const shellStateRef = useRef(null)
  shellStateRef.current = { mode: sidebarMode, overlayOpen, focusMode }
  useEffect(() => {
    const onFocusSearch = () => {
      const s = shellStateRef.current
      if (!s || s.overlayOpen || (s.mode === 'full' && !s.focusMode)) return
      setMenuOverlay('search')
    }
    window.addEventListener('focus-global-search', onFocusSearch)
    return () => window.removeEventListener('focus-global-search', onFocusSearch)
  }, [])

  const sidebarWidthClass = desktopCollapsed
    ? 'w-16'
    : (sidebar_width === 'wide' ? 'w-72' : 'w-64')
  // The page column starts where the menu ends: nothing under the drawer
  // (it floats over the page), the rail's 4rem for the rail (also while it is
  // open over the page), the menu's width for the full menu.  The theme's
  // Minimal style draws the full menu as a 4rem strip that widens on hover
  // (index.css .sidebar-container) — over the page, so the page starts at
  // 4rem there too; it used to start at 16rem and leave 12rem of empty space.
  // Set from the mode (JS), not a CSS breakpoint, so `print:ml-0` resets it
  // on paper.
  const sidebarMarginClass = isDrawer
    ? 'ml-0'
    : (isRail || sidebar_style === 'minimal')
      ? 'ml-16'
      : (sidebar_width === 'wide' ? 'ml-72' : 'ml-64')
  // Floating (margined, rounded) only while the menu sits beside the page.
  const sidebarFloating = sidebar_style === 'floating' && !isDrawer && !overlayOpen
    ? 'm-3 rounded-xl overflow-hidden h-[calc(100%-1.5rem)]'
    : ''
  // How the menu sits: the drawer slides in from the left; while closed it is
  // off-screen and `inert` (below), so nothing in it can be tabbed to or read
  // out.  Deliberately NOT visibility:hidden as well: with "reduce motion" on,
  // index.css gives every element a 0.01ms transition of `all`, so each child
  // animated the inherited visibility and stayed hidden — unfocusable — for a
  // frame after opening, and the drawer could not take focus.  The rail
  // opened over the page just gets a shadow.
  const sidebarPlacement = isDrawer
    ? (overlayOpen ? 'translate-x-0 shadow-2xl transition-transform' : '-translate-x-full transition-transform')
    : (overlayOpen ? 'shadow-2xl' : '')
  // The Minimal style's hover-expand belongs to the full menu at rest only;
  // on the drawer it would squeeze the open drawer to 4rem.
  const sidebarStyleHook = sidebarMode === 'full' && !overlayOpen ? 'sidebar-container' : ''
  const [showTimePopup, setShowTimePopup] = useState(false)
  // Which form the browser's in-page timer popup holds (nextInPageForm).
  const [inPage, setInPage] = useState({ nonce: 0, key: null, seed: null, mounted: false })
  // Read when a ⏸'s form arrives (inPageWorkBesides): the form as last
  // rendered, and its frame's DOM (the typed fields).
  const inPageRef = useRef(inPage)
  inPageRef.current = inPage
  const inPageBoxRef = useRef(null)
  // Ship 6e — Quick Intake modal (header ⚡ button + Ctrl/Cmd+Shift+I)
  const [showQuickIntake, setShowQuickIntake] = useState(false)
  const canQuickIntake = isAdmin(user) || user?.permissions?.can_manage_leads
  const { isEnabled: isFeatureOn } = useFeatureFlags()

  // The menu over the page (drawer / rail opened for search): focus moves in
  // and stays in, Escape and a click outside close it, focus comes back.
  const sidebarRef = useRef(null)
  const menuButtonRef = useRef(null)
  const railSearchRef = useRef(null)
  useOverlayMenu({
    open: overlayOpen,
    containerRef: sidebarRef,
    onClose: closeMenuOverlay,
    focusTarget: (root) => (menuOverlay === 'search' && root ? root.querySelector('#global-search') : null),
    // Where focus goes back when what had it is gone: the ☰ button, or the
    // rail's 🔍 (a new element once the rail is drawn again).
    returnFocusRef: isDrawer ? menuButtonRef : railSearchRef,
    // The rail stays on screen after it closes: a rail control that kept
    // focus keeps it.  The drawer leaves, so focus inside it comes back out.
    keepFocusInside: !isDrawer,
  })
  // Closes when a page is chosen in it (also the page already open, which
  // changes no route).
  const closeMenuOnLinkClick = (e) => {
    const a = e && e.target && typeof e.target.closest === 'function' ? e.target.closest('a[href]') : null
    if (a && sidebarRef.current && sidebarRef.current.contains(a)) setMenuOverlay(null)
  }

  // Recent-item muscle-memory shortcuts — top 5 most recently opened
  // matters/clients/contacts rendered above the main nav (in the icon rail:
  // the ⭐ popover; components/SidebarQuickLists.js draws both).  Each tracker
  // dispatches its own custom event on writes; the native 'storage' event
  // catches cross-tab (multi-window Electron) updates.
  const [recentMatters,  setRecentMatters]  = useState(() => getRecentMatters())
  const [recentClients,  setRecentClients]  = useState(() => recentClientsTracker.get())
  const [recentContacts, setRecentContacts] = useState(() => recentContactsTracker.get())
  // Pinned matters — separate from Recent (no auto-expiry, stays until
  // explicitly unpinned).  Rendered above the Recent block so pinned matters
  // are always the top of the sidebar.
  const [pinnedMatters, setPinnedMatters] = useState(() => getPinnedMatters())
  useEffect(() => {
    const refreshMatters  = () => setRecentMatters(getRecentMatters())
    const refreshClients  = () => setRecentClients(recentClientsTracker.get())
    const refreshContacts = () => setRecentContacts(recentContactsTracker.get())
    const refreshPinned   = () => setPinnedMatters(getPinnedMatters())
    const refreshAll = () => { refreshMatters(); refreshClients(); refreshContacts(); refreshPinned() }
    // Only these keys (or a bulk clear, key null).  Every read returns a new
    // array, so refreshing on ANY storage event re-rendered the whole shell
    // on every other window's write — timer slots, and each window's timer
    // heartbeat every 5 s.
    const onStorage = (e) => {
      const k = e ? e.key : null
      if (k != null && !SIDEBAR_RECENT_KEYS.has(k)) return
      refreshAll()
    }
    window.addEventListener('recent-matters-updated',  refreshMatters)
    window.addEventListener('recent-clients-updated',  refreshClients)
    window.addEventListener('recent-contacts-updated', refreshContacts)
    window.addEventListener(PINNED_MATTERS_EVENT,      refreshPinned)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('recent-matters-updated',  refreshMatters)
      window.removeEventListener('recent-clients-updated',  refreshClients)
      window.removeEventListener('recent-contacts-updated', refreshContacts)
      window.removeEventListener(PINNED_MATTERS_EVENT,      refreshPinned)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  // The menu over the page closes on any page change (a nav item, Back, a
  // shortcut) and whenever the window changes the menu's shape (a drawer left
  // open on a window that is then widened).
  useEffect(() => { setMenuOverlay(null) }, [location.pathname])
  useEffect(() => { setMenuOverlay(null) }, [sidebarMode])

  // Draggable floating button position.  `fabPos` is the position the person
  // CHOSE (dragged to) and is what gets persisted; null = never dragged → the
  // rem-based default.  Feedback #834 (James, 2026-09-30): the chosen offsets
  // used to be rendered verbatim, so a button dragged left on a wide window
  // ended up past the left edge once the window was made narrower (or past
  // the top once shorter) — unreachable.  What is DRAWN is now `fabDisplayed`,
  // the chosen position clamped into the current window and recomputed on
  // resize; it is never written back, so growing the window again returns the
  // button to where it was put.  See utils/fabPosition.js.
  const [fabPos, setFabPos] = useState(() => {
    try { return parseSavedFabPosition(localStorage.getItem('lextrack_fab_pos')) } catch { return null }
  })
  const fabDragState = useRef(null)
  const fabBtnRef = useRef(null)
  // Window size + root font size + the button's real size.  The button is
  // w-12 (3rem) and the root size follows the font-size preference (13-18 px),
  // so the old hard-coded 48 was wrong for most users.
  const [fabViewport, setFabViewport] = useState(() => readFabViewport(null))
  // Desktop 1.0.13 timer bar (James, 2026-10-01): when the desktop app's
  // floating timer bar is on, IT is the timer launcher — it can sit on any
  // monitor, which this in-page button never can.  The button comes back the
  // moment the person hides the bar (tray / right-click), and on desktop
  // 1.0.12 and in a browser there is no bar, so nothing changes there.
  // Folded into fabVisible (not checked at render) so the #834 measuring
  // effect below re-runs when the button reappears.
  const timerBarState = useTimerBarState()
  const fabVisible = hasPermission(user, 'time_entries') && !showTimePopup && !timerBarReplacesFab(timerBarState)
  useEffect(() => {
    const update = () => {
      const next = readFabViewport(fabBtnRef.current)
      setFabViewport(prev => (sameFabViewport(prev, next) ? prev : next))
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    // A font-size preference change resizes the button (and moves the rem
    // default) without any window resize event.
    let ro = null
    if (typeof ResizeObserver !== 'undefined' && fabBtnRef.current) {
      ro = new ResizeObserver(update)
      ro.observe(fabBtnRef.current)
    }
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
      if (ro) ro.disconnect()
    }
  }, [fabVisible])
  // "Reset UI preferences" (lib/uiPrefs.js) removes lextrack_fab_pos and fires a
  // synthetic `storage` event with key null; re-read so the button goes back
  // to the default now rather than at the next reload.  Also keeps a second
  // tab in step.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== null && e.key !== 'lextrack_fab_pos') return
      try { setFabPos(parseSavedFabPosition(localStorage.getItem('lextrack_fab_pos'))) } catch {}
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
  const fabDisplayed = clampFabPosition(fabPos, fabViewport, fabViewport.size, defaultFabPosition(fabViewport.rem))

  const handleFabMouseDown = (e) => {
    e.preventDefault()
    const startX = e.clientX
    const startY = e.clientY
    // Drag from what is on screen (the clamped position), not the stored one:
    // a chosen position that is currently clamped would otherwise jump.  This
    // is a deliberate start-of-drag snapshot — persistence is NOT done from
    // these closures (see onUp).  offsetWidth, not the bounding rect, because
    // hover:scale-110 inflates the rect.
    const startPos = fabDisplayed
    const size = e.currentTarget.offsetWidth || fabViewport.size
    fabDragState.current = { moved: false }
    const onMove = (ev) => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      if (!fabDragState.current.moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) fabDragState.current.moved = true
      if (!fabDragState.current.moved) return
      // Stored as right/bottom offsets so the button stays anchored to the
      // bottom-right on resize; clamped to the window as it is right now.
      setFabPos(fabPositionFromDrag(startPos, dx, dy, { width: window.innerWidth, height: window.innerHeight }, size))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      // Persistence is handled by the useEffect on `fabPos` below.  DO NOT
      // save `fabPos` from here — this closure captured the STALE value at
      // drag-start (either `null` on first drag or the previous position on
      // subsequent drags), and writing it here overwrites every fresh save
      // the useEffect made during the drag itself.  That's why Shawn's fab
      // kept snapping back to its previous position on re-login (feedback
      // received 2026-08-20 evening).
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // Open a timer FORM in a new pop-out window (desktop app only — a browser
  // shows the in-page form).  Used for a blank form and for the forms
  // open-time-entry-popup asks for; an existing timer's form goes through
  // openTimerForm instead (lib/timerWindows.js), which focuses a window
  // already on it.
  const spawnTimerWindow = (route = '/popup/timer/new', popupKey) => {
    // safeOpenPopupWindow runs sessionStorage→localStorage migration first
    // so pre-multi-window-deploy users don't get bounced to /login.
    // Feedback #73.
    const uniq = popupKey || `timer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    // Sizes + the version-gated boundsKey live in timerWindowOptions() so
    // this path and useTimerEventBridge (popped-out windows) cannot drift.
    const opts = timerWindowOptions()
    // Desktop 1.0.13 timer bar (James, 2026-10-01): tell the window which key
    // it was opened under (?pk=).  It records that key for the floating bar,
    // whose ↗ can then focus THIS window — and whose "reopen" lands on this
    // window instead of opening a second one on the same timer.  Without it
    // the bar had no key for any timer started here (the in-page ⏱, a
    // matter's + Add Time, Time Entries' Setup & Start).  Harmless on 1.0.12
    // and in a browser: PopupTimer only passes it along.
    return safeOpenPopupWindow(uniq, tagRouteWithPopupKey(route, uniq), opts)
  }

  // ── Timers (docs/specs/timers-one-click-spec.md, Track L) ────────────────
  // The SLOT is the clock: every timer change goes through lib/timerStore.js,
  // from whichever surface the person used.  Layout holds no clock and no
  // elapsed state — the chip (TimersPanel) and the in-page popup's title own
  // their 1 s ticks.  <TimersWatch> (rendered below: useTimers, events only)
  // runs this window's debounced one-running reconciler and reports whether
  // one of my timers ticks — the ⏱ pulse.  Only that boolean lives in Layout
  // state, so other windows' heartbeats do not re-render the whole shell.
  const [anyTicking, setAnyTicking] = useState(() => readMyTimers().some(r => r.ticking))
  const canPostTime = hasPermission(user, 'can_post_time')
  // The desktop app's main window opens every timer form in a pop-out
  // (openTimerForm / spawnTimerWindow) and renders no in-page timer; a
  // browser shows the in-page form.
  const desktopPopouts = !!window.electronAPI?.openPopupWindow

  // The browser's in-page form (nextInPageForm, above).
  const showInPage = (request) => {
    setInPage(prev => nextInPageForm(prev, request))
    setShowTimePopup(true)
  }
  const hideTimePopup = () => setShowTimePopup(false)
  // The form bound itself (START made a slot; Save / Discard dropped it;
  // Continue seeded a resume slot): follow it, without a remount.
  const handleInPageRebind = (key) => {
    setInPage(prev => {
      const next = isTimerSlotKey(key) ? key : null
      return prev.key === next ? prev : { ...prev, key: next }
    })
  }
  // What swapping the in-page form for timer `key`'s would lose (null =
  // nothing): 'saving' — it is bound to another timer whose save is in flight
  // (a fresh claim); 'draft' — it is unbound with a description or hours
  // typed.  TimerWidget gives those fields id={fid('description')} /
  // id={fid('hours')}, ids ending '-description' / '-hours' (pinned in
  // layoutTimer.source.test.js); a prefilled matter or rate is not work.
  const inPageWorkBesides = (key) => {
    const cur = inPageRef.current
    if (!cur || !cur.mounted || cur.key === key) return null
    if (cur.key != null) {
      let row = null
      try { row = timerStore.getTimer(cur.key) } catch { row = null }
      return row && row.claimed ? 'saving' : null
    }
    const box = inPageBoxRef.current
    if (!box || typeof box.querySelectorAll !== 'function') return null
    const typed = Array.from(box.querySelectorAll('textarea[id$="-description"], input[id$="-hours"]'))
      .some(el => String(el.value == null ? '' : el.value).trim() !== '')
    return typed ? 'draft' : null
  }
  // The in-page form on timer `key` (Fill in & save, ⏸, …).  `how.afterPause`
  // (openFormAfterPause — a person's ⏸): a form holding work stays, shown,
  // with a PAUSE_FORM_KEPT toast.  Fill in & save is a choice: it swaps.
  const openSlotInPage = (key, how) => {
    const work = how && how.afterPause ? inPageWorkBesides(key) : null
    if (work) {
      setShowTimePopup(true)
      toast.info(PAUSE_FORM_KEPT[work])
      return
    }
    showInPage({ kind: 'slot', key })
  }

  // One timer's form: a pop-out on the desktop (focused when one is already
  // open on it), the in-page form in a browser.  A timer written by old code
  // is adopted first (openTimerFormFor — silently where no old window can
  // hold it, after a confirm where one might).
  const openRowForm = (row) => openTimerFormFor(row, {
    ask, toast, onOpenInPage: (key) => showInPage({ kind: 'slot', key })
  })
  // A blank form: a new pop-out on the desktop, the in-page form in a browser.
  const openBlankForm = () => {
    if (desktopPopouts) { spawnTimerWindow('/popup/timer/new'); return }
    showInPage({ kind: 'blank' })
  }

  // A row's Resume in a browser: the entry's ONE resume slot
  // ('lextrack_timer:resume:<id>').  A live one is restored, never re-seeded;
  // otherwise it is seeded PAUSED from GET /:id — the row's own copy lacks
  // the attorney, phase, date and updated_at (the resume version check).
  const openResumeInPage = async (resume) => {
    const key = resumeSlotKey(resume && resume.id)
    if (!key) return
    const existing = timerStore.getTimer(key)
    if (existing) {
      if (!existing.mine) { toast.warn(MESSAGES.foreign); return }
      await openRowForm(existing)
      return
    }
    let entry = null
    try {
      const res = await api.get(`/api/time-entries/${encodeURIComponent(String(resume.id))}`)
      entry = res && res.data && res.data.id != null ? res.data : null
    } catch { entry = null }
    if (!entry) { toast.error("Couldn't load that entry"); return }
    const seconds = Math.max(0, Math.round((parseFloat(entry.hours) || 0) * 3600))
    const r = timerStore.seedResume(entry.id, { entry, seedSeconds: seconds, entryUpdatedAt: entry.updated_at ?? null })
    if (!r.ok) { reportStoreResult(r, { toast }); return }
    showInPage({ kind: 'slot', key: r.key, seed: { resumeData: entry } })
  }

  const handleFabClick = () => {
    // Swallow click if it was actually a drag
    if (fabDragState.current?.moved) { fabDragState.current = null; return }
    // The ticking timer's form, otherwise a blank form.
    const ticking = readMyTimers().find(r => r.ticking)
    if (ticking) { openRowForm(ticking); return }
    openBlankForm()
  }

  // Window events (registered once below; they read this ref, never closure
  // state).  A main-process control needs its listener here or it silently
  // does nothing (desktop.md).
  //   'start-timer' {matter_id?, matter_title?, billing_type?} — a one-click,
  //       HEADLESS start (Dashboard 'Start Timer', Time Entries' empty state,
  //       Ctrl+K 'Start timer', a matter's Start timer): no window opens and
  //       focus is not stolen.  Without can_post_time: a toast, nothing made.
  //   'tray-start-timer' — the tray's Start Timer: the same one-click start.
  //   'global-toggle-timer' — Ctrl/Cmd+Shift+T (firm decision 2026-10-13):
  //       pause my running timer, otherwise start a new one.  A pause opens
  //       that timer's form here, as Fill in & save does (James, 2026-10-15),
  //       unless the in-page form holds unsaved work (openSlotInPage); a
  //       start opens none.
  //   'tray-stop-timer' — the tray's "Open Running Timer": that timer's form.
  //       Never a save from the tray (saving needs a description).
  //   'open-time-entry-popup' — a FORM (see its handler below).
  const timerShortcutRef = useRef(null)
  timerShortcutRef.current = { canTime: hasPermission(user, 'time_entries'),
    start: (detail) => startOneClickTimer({ user, detail, toast, ask }),
    toggle: () => toggleOneClickTimer({ user, toast, ask, openForm: { onOpenInPage: openSlotInPage } }),
    // The tray reads only the legacy slot, so its "Open Running Timer" means
    // that one when it is mine; otherwise my running (else latest) timer.
    openRunning: () => {
      const legacy = timerStore.getTimer(LEGACY_SLOT_KEY)
      const list = readMyTimers()
      const row = legacy && legacy.mine && !legacy.hidden ? legacy : (list.find(r => r.ticking) || list[0])
      if (row) openRowForm(row)
      else openBlankForm()
    },
    // A window still on old code holds it (v1-held): 'focus' is addressed to
    // that window, which answers it itself.  Opening it from here would send
    // 'focus' again, which this window's own listener hears — a loop.
    openLegacy: () => {
      const row = timerStore.getTimer(LEGACY_SLOT_KEY)
      if (row && row.mine && row.cls !== 'v1-held') openRowForm(row)
    },
    prefill: (detail) => showInPage({ kind: 'prefill', matterId: detail && detail.matter_id, rate: detail && detail.rate }),
    resume: (resume) => openResumeInPage(resume)
  }
  useEffect(() => {
    // One action per keypress: in the desktop app Ctrl+Shift+T can arrive
    // through the global shortcut AND the menu accelerator (and, if the OS
    // refused the global registration, the web keyboard hook too) — without
    // this, one keypress could start a timer and pause it again.
    let lastOpenAt = 0
    const once = (run) => () => {
      const s = timerShortcutRef.current
      if (!s || !s.canTime) return
      const now = Date.now()
      if (now - lastOpenAt < 1000) return
      lastOpenAt = now
      run(s)
    }
    const onTrayStart = once((s) => s.start())
    const onToggle = once((s) => s.toggle())
    const onOpenRunning = once((s) => s.openRunning())
    // Pages: gated on can_post_time inside startOneClickTimer, which has its
    // own 1 s double-fire guard (shared with the chip and the panel).
    const onStartTimer = (e) => {
      const s = timerShortcutRef.current
      if (s) s.start(e && e.detail)
    }
    window.addEventListener('tray-start-timer', onTrayStart)
    window.addEventListener('global-toggle-timer', onToggle)
    window.addEventListener('tray-stop-timer', onOpenRunning)
    window.addEventListener('start-timer', onStartTimer)
    return () => {
      window.removeEventListener('tray-start-timer', onTrayStart)
      window.removeEventListener('global-toggle-timer', onToggle)
      window.removeEventListener('tray-stop-timer', onOpenRunning)
      window.removeEventListener('start-timer', onStartTimer)
    }
  }, [])

  // The legacy timer ('lextrack_timer', the old in-page timer's slot).  An old
  // floating bar (until the app restarts) and openTimerForm send it 'focus'
  // to bring up "the main window's timer": show its form here (adopting it
  // first).  Always on — the command channel no longer depends on the bar
  // (lib/timerCommands.js), and desktop 1.0.11/1.0.12 pop-out pages send it
  // too.
  const legacyFocusGuard = useRef(null)
  if (!legacyFocusGuard.current) legacyFocusGuard.current = createOneClickGuard({ ms: LEGACY_FOCUS_MS })
  useTimerCommandListener(LEGACY_SLOT_KEY, (cmd) => {
    if (cmd.action !== 'focus') return
    const s = timerShortcutRef.current
    if (!s || !s.canTime || !legacyFocusGuard.current.tryFire()) return
    s.openLegacy()
  })

  // Desktop main window: the legacy slot can only have been THIS window's old
  // in-page timer (single instance; pop-outs never held it), and this window
  // now runs the new code, so nothing else can be counting it.  Adopt it
  // silently (classify → 'adopt-silently'): it becomes an ordinary timer that
  // opens in a pop-out.  Browsers never do this — another old tab may still
  // tick it, so there the first action on it asks first.
  useEffect(() => {
    if (!user?.id || !window.electronAPI?.isElectron) return
    let c = null
    try { c = timerStore.classify(LEGACY_SLOT_KEY) } catch { c = null }
    if (!c || c.cls !== 'adopt-silently') return
    timerStore.adoptV1(LEGACY_SLOT_KEY).then((r) => {
      if (r && !r.ok && r.reason === 'storage') reportStoreResult(r, { toast })
    }).catch((err) => console.error('legacy timer adoption failed', err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // One toast per sign-in for paused timers idle > 30 min (pausedAt) — the
  // in-page popup no longer reopens itself on load.  No matter names: it can
  // appear during a screen share (Rule 1.6).
  const staleToastShownFor = useRef(null)
  useEffect(() => {
    if (!user?.id || !canPostTime) return undefined
    const uid = String(user.id)
    if (staleToastShownFor.current === uid) return undefined
    const now = Date.now()
    const stale = readMyTimers().filter(r => {
      if (!r.slot || !r.slot.paused || !(r.elapsed > 0)) return false
      const at = Number(r.slot.pausedAt) || Number(r.slot.startTime) || now
      return now - at > STALE_PAUSE_MS
    })
    if (!stale.length) return undefined
    const oldest = Math.min(...stale.map(r => Number(r.slot.pausedAt) || Number(r.slot.startTime) || now))
    const age = now - oldest
    const ago = age > 24 * 3600 * 1000 ? `${Math.floor(age / (24 * 3600 * 1000))}d` : `${Math.max(1, Math.floor(age / (3600 * 1000)))}h`
    const hrs = (stale.reduce((sum, r) => sum + (Number(r.elapsed) || 0), 0) / 3600).toFixed(1)
    const n = stale.length
    const msg = n === 1
      ? `⏱ You have a paused timer (${hrs} hr, paused ${ago} ago). Open ⏱ Timers to save or resume it.`
      : `⏱ You have ${n} paused timers (${hrs} hr in all, the oldest paused ${ago} ago). Open ⏱ Timers to save or resume them.`
    const t = setTimeout(() => {
      staleToastShownFor.current = uid
      try { toast.info(msg, { duration: 12000 }) } catch { /* toast not ready */ }
    }, 800)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, canPostTime])

  // Persist the CHOSEN position whenever a drag changes it.  Only `fabPos` is
  // ever written — never `fabDisplayed` — so a resize that clamps the button
  // does not overwrite where the person put it (feedback #834).
  useEffect(() => {
    if (fabPos) {
      try { localStorage.setItem('lextrack_fab_pos', JSON.stringify(fabPos)) } catch {}
    }
  }, [fabPos])
  // Ship 6e — Ctrl/Cmd+Shift+I opens the Quick Intake modal.  Not Ctrl+I
  // (browser italic) or F12 (Electron devtools).  Ctrl+Shift+I is unused
  // in Chromium/Electron for authenticated app-level actions.  Skips
  // typing targets (input/textarea/select/contentEditable) so the shortcut
  // doesn't fire while the user is composing a message.
  useEffect(() => {
    if (!canQuickIntake) return
    const onKey = (e) => {
      if (!((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i'))) return
      const tag = (e.target?.tagName || '').toLowerCase()
      const editable = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable
      if (editable) return
      e.preventDefault()
      setShowQuickIntake(true)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [canQuickIntake])

  // Also expose a window event so other components (e.g. Leads.js empty
  // state) can trigger the modal without importing Layout state.
  useEffect(() => {
    const handler = () => { if (canQuickIntake) setShowQuickIntake(true) }
    window.addEventListener('open-quick-intake', handler)
    return () => window.removeEventListener('open-quick-intake', handler)
  }, [canQuickIntake])

  // open-time-entry-popup asks for a FORM ('+ Add Time', 'Setup & Start', the
  // n/t shortcuts, Ctrl+K 'New time entry', a row's Resume).
  //   Desktop: a pop-out per request, as before (a resume under
  //     'timer-resume-<id>', so a second click focuses it).
  //   Browser: the in-page form.  It NEVER retargets the slot it is on: a
  //     timer the form holds keeps running in its slot, and the form comes
  //     back unbound and prefilled (nextInPageForm 'prefill').  A Resume opens
  //     that entry's own resume slot.
  useEffect(() => {
    const handler = (e) => {
      const detail = (e && e.detail) || {}
      const resume = detail.resume_entry
      if (window.electronAPI?.openPopupWindow) {
        if (resume?.id) {
          // Deterministic key so re-clicking Resume on the same entry
          // focuses the existing window instead of opening a duplicate
          spawnTimerWindow(`/popup/timer/resume/${resume.id}`, `timer-resume-${resume.id}`)
        } else {
          const params = new URLSearchParams()
          if (detail.matter_id) params.set('matter_id', String(detail.matter_id))
          if (detail.rate)      params.set('rate', String(detail.rate))
          const qs = params.toString()
          spawnTimerWindow(`/popup/timer/new${qs ? `?${qs}` : ''}`)
        }
        return
      }
      const s = timerShortcutRef.current
      if (!s) return
      if (resume && resume.id != null) { s.resume(resume); return }
      s.prefill(detail)
    }
    window.addEventListener('open-time-entry-popup', handler)
    return () => window.removeEventListener('open-time-entry-popup', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-minimize the timer popup when another popup/dialog opens, so the
  // timer doesn't cover what the user wants to look at.  Dispatched by
  // DraggablePopup on mount (every other usage), so opening a matter
  // quick-view, calendar event, etc. collapses the timer to its fab.
  // The timer keeps running in its slot; the fab stays visible.
  useEffect(() => {
    const handler = () => setShowTimePopup(false)
    window.addEventListener('minimize-time-popup', handler)
    return () => window.removeEventListener('minimize-time-popup', handler)
  }, [])

  // Sidebar nav: right-click (Surface A) shows a small context menu with
  // Open / Open in new window options.  Ctrl+click (Surface D) is the
  // browser shortcut for the "Open in new window" action — bypasses the
  // menu and goes straight to spawning a new BrowserWindow.  Both open
  // `/popup/page<route>` in its own OS window with its own taskbar entry.
  // Falls through to default behavior in the browser (no electronAPI).
  const [navCtxMenu, setNavCtxMenu] = useState(null)

  const openNavInPopup = (item) => {
    const route = item.path.startsWith('/') ? item.path : `/${item.path}`
    const popupRoute = `/popup/page${route}`
    const popupKey = `page-${route}`
    safeOpenPopupWindow(popupKey, popupRoute, { width: 1200, height: 800 })
  }

  const handleNavContextMenu = (e, item) => {
    // Always intercept right-click on nav items.  The "Open in new window"
    // option is hidden in browsers (no electronAPI), but the menu itself
    // still appears with just "Open" so users get a consistent experience.
    e.preventDefault()
    e.stopPropagation()
    const items = [{ label: 'Open', icon: '↗', onClick: () => navigate(item.path) }]
    if (window.electronAPI?.openPopupWindow) {
      items.push({ label: 'Open in new window', icon: '⤴', onClick: () => openNavInPopup(item) })
    }
    setNavCtxMenu({ x: e.clientX, y: e.clientY, items })
  }

  const handleNavClick = (e, item) => {
    if (!window.electronAPI?.openPopupWindow) return
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      openNavInPopup(item)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex" data-shell-mode={sidebarMode}>
      {/* Skip-to-main-content link — WCAG 2.4.1 (Bypass Blocks).  Hidden
          off-screen until keyboard-focused via Tab from the top of the page.
          Jumps focus to the <main> landmark so screen-reader/keyboard users
          skip the sidebar chrome. */}
      <a href="#main-content" className="skip-link no-print">
        Skip to main content
      </a>
      {/* Narrow windows (drawer mode): the ☰ button, fixed top-left so it is
          reachable however far the page is scrolled.  The page's top row
          leaves room for it (see the spacer in <main>).  While the drawer is
          open its own ✕ takes over: this one drops under the backdrop and the
          drawer (z-index, not visibility:hidden — with "reduce motion" the
          visibility change animates and focus could not come back to it on
          Esc). */}
      {isDrawer && (
        <button
          ref={menuButtonRef}
          type="button"
          onClick={() => setMenuOverlay('menu')}
          aria-label="Open menu"
          aria-expanded={overlayOpen}
          aria-controls="app-sidebar"
          title="Menu"
          className={`no-print fixed top-3 left-3 ${overlayOpen ? 'z-[58]' : 'z-[65]'} w-10 h-10 rounded-lg bg-primary-700 text-white shadow-lg hover:bg-primary-800 flex items-center justify-center text-xl`}>
          ☰
        </button>
      )}

      {/* Backdrop behind the menu over the page — a click outside closes it. */}
      {overlayOpen && (
        <div
          data-testid="sidebar-backdrop"
          onClick={closeMenuOverlay}
          className="no-print fixed inset-0 z-[59] bg-black/40"
          aria-hidden="true"
        />
      )}

      {/* Sidebar — the full menu, the icon rail, or the drawer.  Over the
          page (drawer / rail opened for search) it is a modal dialog; the
          closed drawer is inert so nothing in it can be tabbed to.  Calendar's
          full screen (focusMode) hides the menu beside the page; the drawer
          still opens there. */}
      <div
        id="app-sidebar"
        ref={sidebarRef}
        role={overlayOpen ? 'dialog' : undefined}
        aria-modal={overlayOpen ? 'true' : undefined}
        aria-label={overlayOpen ? 'Main menu' : undefined}
        aria-hidden={isDrawer && !overlayOpen ? 'true' : undefined}
        inert={isDrawer && !overlayOpen ? true : undefined}
        onClickCapture={overlayOpen ? closeMenuOnLinkClick : undefined}
        className={`${sidebarStyleHook} ${focusMode && !isDrawer && !overlayOpen ? 'hidden' : ''} ${sidebarWidthClass} ${isDrawer ? 'max-w-[85vw]' : ''} bg-primary-700 text-white flex flex-col fixed top-0 left-0 h-full z-[60] duration-200 ${sidebarPlacement} ${sidebarFloating}`}>
        {/* Logo — in the rail its buttons stack in one column (three side by
            side are wider than the 4rem rail).  On a short window (760 px
            high or less) the rail's header and footer tighten their spacing
            (the [@media(max-height:760px)] classes here and in the footer),
            so more page icons fit between them; nothing is hidden. */}
        <div className={`${desktopCollapsed ? 'px-2 py-3 [@media(max-height:760px)]:py-1.5' : 'px-6 py-5'} border-b border-primary-600 shrink-0`}>
          <div className={desktopCollapsed ? 'flex flex-col items-center gap-1' : 'flex items-start justify-between gap-2'}>
            {!desktopCollapsed && (
              <div className="min-w-0">
                <h1 className="text-2xl font-bold">Omnia</h1>
                <p className="text-primary-100 text-xs mt-1 mb-3">Practice Management</p>
              </div>
            )}
            <div className={desktopCollapsed ? 'flex flex-col items-center gap-1' : 'flex items-center gap-1 shrink-0 ml-auto'}>
              {/* Contextual help — moved here from a top-right floating position
                  because that spot collided with the "+ New X" button on many
                  list pages.  Hidden on routes that have no help doc (/help,
                  /popup/*) since the button would be a dead-end. */}
              {(() => {
                const hasHelp = !!slugForPath(location.pathname)
                    && location.pathname !== '/help'
                    && !location.pathname.startsWith('/help#')
                    && !location.pathname.startsWith('/popup/')
                if (!hasHelp) return null
                return (
                  <button
                    onClick={() => window.dispatchEvent(new Event('open-contextual-help'))}
                    title="Help for this page (? key)"
                    aria-label="Show help for this page"
                    data-rail-tip={desktopCollapsed ? 'Help for this page' : undefined}
                    className="text-primary-100 hover:text-white hover:bg-primary-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold transition-colors">
                    ?
                  </button>
                )
              })()}
              {/* « / » — the person's own full-menu / icon-rail choice for
                  this window size.  Over the page (drawer, or the rail opened
                  for search) a ✕ closes the menu instead; it takes focus when
                  the menu opens. */}
              {overlayOpen ? (
                <button
                  type="button"
                  onClick={closeMenuOverlay}
                  data-autofocus
                  title="Close menu (Esc)"
                  aria-label="Close menu"
                  className="text-primary-100 hover:text-white hover:bg-primary-600 rounded-full w-8 h-8 flex items-center justify-center text-lg transition-colors">
                  ✕
                </button>
              ) : !isDrawer && (
                <button
                  type="button"
                  onClick={toggleDesktopCollapsed}
                  title={desktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar to icons'}
                  data-rail-tip={desktopCollapsed ? 'Expand sidebar' : undefined}
                  className="flex text-primary-100 hover:text-white hover:bg-primary-600 rounded-full w-8 h-8 items-center justify-center text-sm transition-colors"
                  aria-label={desktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                  {desktopCollapsed ? '»' : '«'}
                </button>
              )}
              <button
                onClick={() => window.location.reload()}
                title="Refresh app (Ctrl+R) — re-fetches all data; any in-progress form drafts will be lost"
                data-rail-tip={desktopCollapsed ? 'Refresh app' : undefined}
                className="text-primary-100 hover:text-white hover:bg-primary-600 rounded-full w-8 h-8 flex items-center justify-center text-lg transition-colors"
                aria-label="Refresh app">
                🔄
              </button>
            </div>
          </div>
          {/* The search box, or in the rail a 🔍 that opens the menu over the
              page with the box focused (as Ctrl+K and "/" do). */}
          {desktopCollapsed ? (
            <button
              ref={railSearchRef}
              type="button"
              onClick={() => setMenuOverlay('search')}
              title="Search (Ctrl+K)"
              aria-label="Search (Ctrl+K)"
              data-rail-tip="Search (Ctrl+K)"
              className="mx-auto mt-2 [@media(max-height:760px)]:mt-1 text-primary-100 hover:text-white hover:bg-primary-600 rounded-full w-8 h-8 flex items-center justify-center text-sm transition-colors"
            >🔍</button>
          ) : <GlobalSearch />}
          {/* The rail's ⭐: Pinned + Recent in a popover (the full menu and
              the drawer show them in the menu itself, below). */}
          {desktopCollapsed && (
            <RailQuickMenu
              user={user}
              pathname={location.pathname}
              pinnedMatters={pinnedMatters}
              recentMatters={recentMatters}
              recentClients={recentClients}
              recentContacts={recentContacts}
            />
          )}
          {/* Ship 6e — Quick Intake header button.  Full label when expanded,
              icon-only when collapsed to icons.  Ctrl/Cmd+Shift+I fires the
              same action. */}
          {canQuickIntake && (
            desktopCollapsed ? (
              <button
                onClick={() => setShowQuickIntake(true)}
                title="Quick Intake (Ctrl/Cmd+Shift+I) — create a lead from a phone call, walk-in, or hallway conversation"
                aria-label="Quick Intake"
                data-rail-tip="Quick Intake"
                className="mx-auto mt-2 [@media(max-height:760px)]:mt-1 text-white bg-amber-500 hover:bg-amber-600 rounded-full w-8 h-8 flex items-center justify-center text-sm shadow transition-colors"
              >⚡</button>
            ) : (
              <button
                onClick={() => setShowQuickIntake(true)}
                title="Quick Intake (Ctrl/Cmd+Shift+I) — create a lead from a phone call, walk-in, or hallway conversation"
                className="mt-2 w-full flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow transition-colors"
              >
                <span>⚡</span>
                <span>Quick Intake</span>
                <kbd className="ml-1 text-[9px] bg-amber-600/50 px-1 py-0.5 rounded font-mono hidden lg:inline">⌘⇧I</kbd>
              </button>
            )
          )}
        </div>

        {/* One scrolling region between the header and the footer: Pinned,
            Recent and the page list scroll together, so on a short window the
            footer (Report Issue, profile, Sign Out) is never pushed below the
            screen by a long Pinned list. */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {/* Pinned Matters, then Recent Matters / Clients / Contacts
            (components/SidebarQuickLists.js).  The icon rail has no room for
            titles, so there the same lists open from the ⭐ button in the
            header above (RailQuickMenu). */}
        {!desktopCollapsed && (
          <SidebarQuickLists
            user={user}
            pinnedMatters={pinnedMatters}
            recentMatters={recentMatters}
            recentClients={recentClients}
            recentContacts={recentContacts}
          />
        )}

        {/* Navigation — the grouped page list (SidebarNav, above). */}
        <SidebarNav
          user={user}
          isFeatureOn={isFeatureOn}
          desktopCollapsed={desktopCollapsed}
          pathname={location.pathname}
          reviewQueueCount={reviewQueueCount}
          acctLockDate={acctLockDate}
          onNavContextMenu={handleNavContextMenu}
          onNavClick={handleNavClick}
        />
        </div>

        {/* User / Logout — collapsed mode shows just the avatar + 3 icon
            buttons stacked; expanded shows the full name + role + labels.
            The icons are named by aria-label (their text is only the emoji). */}
        <div className={`${desktopCollapsed ? 'px-1 [@media(max-height:760px)]:py-1.5' : 'px-4'} py-4 border-t border-primary-600 shrink-0`}>
          {/* Report Issue */}
          <button
            onClick={() => window.dispatchEvent(new Event('open-report-issue'))}
            title={desktopCollapsed ? 'Report Issue' : undefined}
            aria-label={desktopCollapsed ? 'Report Issue' : undefined}
            data-rail-tip={desktopCollapsed ? 'Report Issue' : undefined}
            className={`w-full mb-2 ${desktopCollapsed ? 'justify-center px-0 py-2 [@media(max-height:760px)]:py-1 [@media(max-height:760px)]:mb-0.5' : 'gap-2 px-4 py-2 text-left'} text-sm text-primary-200 hover:text-white hover:bg-primary-600 rounded-lg transition-colors flex items-center`}
          >
            <span>💬</span>{!desktopCollapsed && <span>Report Issue</span>}
          </button>
          {/* Avatar block — always shown; name/title collapse to just the initial */}
          <div className={`flex items-center ${desktopCollapsed ? 'justify-center px-0 py-2 [@media(max-height:760px)]:py-1' : 'gap-3 px-4 py-2'}`}>
            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-sm font-bold shrink-0"
                 title={desktopCollapsed ? `${user?.name || ''} · ${user?.title || user?.role || ''}` : undefined}>
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            {!desktopCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-primary-200 truncate">{user?.title || user?.role}</p>
              </div>
            )}
          </div>
          <NavLink to="/profile"
            title={desktopCollapsed ? 'My Profile' : undefined}
            aria-label={desktopCollapsed ? 'My Profile' : undefined}
            data-rail-tip={desktopCollapsed ? 'My Profile' : undefined}
            className={({ isActive }) =>
              `${desktopCollapsed ? 'flex justify-center px-0 py-2 [@media(max-height:760px)]:py-1' : 'block mt-2 px-4 py-2'} text-sm rounded-lg transition-colors ${
                isActive ? 'bg-primary-500 text-white' : 'text-primary-200 hover:text-white hover:bg-primary-600'
              }`
            }
          >
            {desktopCollapsed ? '👤' : 'My Profile'}
          </NavLink>
          <button
            onClick={logout}
            title={desktopCollapsed ? 'Sign Out' : undefined}
            aria-label={desktopCollapsed ? 'Sign Out' : undefined}
            data-rail-tip={desktopCollapsed ? 'Sign Out' : undefined}
            className={`w-full mt-1 ${desktopCollapsed ? 'flex justify-center px-0 py-2 [@media(max-height:760px)]:py-1 [@media(max-height:760px)]:mt-0' : 'text-left px-4 py-2'} text-sm text-primary-200 hover:text-white hover:bg-primary-600 rounded-lg transition-colors`}
          >
            {desktopCollapsed ? '🚪' : 'Sign Out'}
          </button>
        </div>
      </div>

      {/* Main Content — a fluid column: flex-1 takes what the menu leaves,
          and min-w-0 lets it shrink below its content's width, so a wide
          table inside a page cannot widen the column (and the shell) past
          the window.  The margin matches the menu (sidebarMarginClass);
          print:ml-0 drops it on paper (the menu is not printed). */}
      <div className={`flex-1 min-w-0 ${focusMode ? 'ml-0' : sidebarMarginClass} print:ml-0`}>
        {/* Padding: p-4 under 768 px, p-8 from 768 px up (Tailwind md is the
            same 768 px the drawer stops at), whatever the menu's shape.
            Keep it in step with full-bleed bars that cancel it with a
            negative margin — Matter Detail's sticky tab bar is
            `-mx-4 md:-mx-8 px-4 md:px-8`; a tighter padding here left that
            bar overhanging both edges and the page scrolled sideways
            (Layout.responsive.test.js pins it).  No top padding for the ☰
            button any more — the top row below leaves room beside it.
            id="main-content" is the target of the skip-link at the top of
            this component; role="main" is redundant with <main> but some
            older screen readers benefit from the explicit landmark. */}
        <main id="main-content" role="main" tabIndex={-1}
          className={focusMode ? 'p-2 md:p-3' : 'p-4 md:p-8'}>
          {/* Top row: ← Back on the left, quick-access bar (#831/#832) on the
              right; wraps on narrow screens.

              Feedback #507 — global Back button, hidden on Dashboard.
              Feedback #830 — it used to be a bare navigate(-1), which after
              sign-in walked straight back onto the sign-in form.  resolveBack
              (utils/navHistory.js) only walks back when it knows the previous
              entry is an in-app page; otherwise — first page of the session,
              previous entry is /login, or unknown — it goes to Calendar (then
              Matters, then Dashboard, whichever the user can open and is not
              already on) and says so on the button.  no-print on both. */}
          <div className="no-print mb-3 flex flex-wrap items-center gap-2">
            {/* Narrow windows: room for the fixed ☰ button on the first line
                only (the line wraps under it as a flex item would). */}
            {isDrawer && <span aria-hidden="true" className="w-10 h-8 shrink-0" />}
            {(() => {
              const back = resolveBack({
                idx: window.history.state?.idx ?? 0,
                currentPath: location.pathname,
                history: readNavHistory(),
                can: (p) => hasPermission(user, p),
              })
              if (!back) return null
              const isHistory = back.mode === 'history'
              return (
                <button
                  type="button"
                  onClick={() => (isHistory ? navigate(-1) : navigate(back.path))}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg px-3 py-1.5 transition-colors"
                  title={isHistory ? 'Go back to the previous page (Alt+Left)' : `No earlier page in this session — go to ${back.label}`}
                  aria-label={isHistory ? 'Go back to previous page' : `Go to ${back.label}`}
                >
                  <span aria-hidden="true">←</span> {isHistory ? 'Back' : back.label}
                </button>
              )
            })()}
            {/* flex-1 + min-w-0: shares the first line with ☰'s room and ←
                Back, wrapping its own buttons inside it, instead of dropping
                whole onto a line of its own. */}
            <div className="ml-auto min-w-0 flex-1 flex flex-wrap items-center justify-end gap-2">
              <QuickAccessBar />
              {/* The Timers chip + panel (one-click timers).  Hidden without
                  can_post_time (TimersPanel checks); never shows a matter
                  name (Rule 1.6). */}
              <TimersPanel
                user={user}
                onOpenInPage={openSlotInPage}
                onNewEntry={openBlankForm}
                barVisible={!!(timerBarState?.supported && timerBarState?.visible)}
              />
            </div>
          </div>
          {/* T2-B Phase 1 — inner Suspense + ChunkLoadErrorBoundary wraps
              ONLY the Outlet so a lazy page navigation shows the
              PageLoading fallback in-place while sidebar + timer state
              stay mounted.  Without this the outer Suspense in App.js
              catches instead and the whole shell unmounts on every
              lazy-page nav (Opus 5 finding in the T2-B pre-code review).
              ChunkLoadError bounces to a one-click reload UI. */}
          <ChunkLoadErrorBoundary>
            <Suspense fallback={<PageLoading />}>
              <ResetChunkReloadFlag />
              <Outlet />
            </Suspense>
          </ChunkLoadErrorBoundary>
        </main>
      </div>

      {/* This window's timer watcher: the reconciler, and the ⏱ pulse. */}
      <TimersWatch onAnyTicking={setAnyTicking} />

      {/* Global New Time Entry / Timer Button — draggable.  It pulses while
          any of my timers ticks; a click opens the ticking timer's form,
          otherwise a blank form. */}
      {fabVisible && (
        <button ref={fabBtnRef} onClick={handleFabClick} onMouseDown={handleFabMouseDown}
          style={{ right: `${fabDisplayed.right}px`, bottom: `${fabDisplayed.bottom}px`, cursor: 'grab' }}
          className={`fixed w-12 h-12 ${anyTicking ? 'bg-green-600 hover:bg-green-700 animate-pulse' : 'bg-primary-600 hover:bg-primary-700'} text-white rounded-full shadow-lg flex items-center justify-center z-40 transition-transform hover:scale-110 select-none no-print`}
          title={anyTicking ? 'Timer running — click to open, drag to move' : 'New Time Entry — drag to move'}>
          {anyTicking ? (
            <span className="text-2xl leading-none">⏱</span>
          ) : (
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="13" r="8" />
              <polyline points="11 9 11 13 13.5 14.5" />
              <line x1="8" y1="2" x2="14" y2="2" />
              <line x1="18" y1="5.5" x2="20.5" y2="8" />
              <circle cx="19" cy="5" r="3.5" fill="currentColor" stroke="none" />
              <line x1="19" y1="3.5" x2="19" y2="6.5" stroke="white" strokeWidth="1.5" />
              <line x1="17.5" y1="5" x2="20.5" y2="5" stroke="white" strokeWidth="1.5" />
            </svg>
          )}
        </button>
      )}

      {/* The browser's in-page timer form (the desktop app opens pop-outs
          instead).  Mounted on first use, then kept mounted while hidden;
          `key` changes only when Layout shows a DIFFERENT form
          (nextInPageForm), so the old one unmounts and flushes. */}
      {!desktopPopouts && inPage.mounted && (
        <InPageTimerPopup visible={showTimePopup} slotKey={inPage.key} onHide={hideTimePopup} boxRef={inPageBoxRef}>
          <TimerWidget
            key={inPage.nonce}
            storageKey={inPage.key}
            standalone={false}
            shortcutsEnabled={showTimePopup}
            onRebind={handleInPageRebind}
            onClose={hideTimePopup}
            popupKey={null}
            initialMatterId={inPage.seed?.matterId || ''}
            initialRate={inPage.seed?.rate || ''}
            resumeData={inPage.seed?.resumeData || null}
          />
        </InPageTimerPopup>
      )}

      {/* Global keyboard shortcuts help modal — listens for Ctrl+/ */}
      <KeyboardShortcutsHelp />
      <ReportIssueButton />
      <GlobalReminders />
      <ContextualHelp />
      <OnboardingTour />

      {/* Sidebar nav right-click menu (Open / Open in new window) */}
      <ContextMenu menu={navCtxMenu} onClose={() => setNavCtxMenu(null)} />

      {/* The icon rail's names for keyboard focus (the title covers hover). */}
      <RailTooltip containerRef={sidebarRef} enabled={desktopCollapsed} />

      {/* Ship 6e — Quick Intake modal (⚡ button + Ctrl/Cmd+Shift+I) */}
      {showQuickIntake && canQuickIntake && (
        <QuickIntakeModal onClose={() => setShowQuickIntake(false)} />
      )}
    </div>
  )
}
